<template>
  <div class="absolute right-4 bottom-4">
    <button
      data-cy="floating-button"
      class="z-50 flex h-12 w-12 items-center justify-center rounded-full border border-gray-600 bg-primary-heavy-contrast text-white shadow-lg transition-colors"
      @click="handleFloatingButtonClick"
    >
      <icon-chat v-if="!isMobile && !isChatOpen" />
      <icon-headset v-else-if="isMobile && !isSubmenuOpen && !isChatOpen" />
      <icon-x v-else />
    </button>
    <div class="absolute -top-16 right-0">
      <popup-chat-toast
        :show="showToast"
        data-cy="chat-toast"
        :message="t('chat.greeting')"
        @close="isToastTriggered = false"
      />
    </div>
  </div>

  <div class="absolute bottom-20 left-0 z-40 w-full">
    <platform-floating-button-submenu
      v-if="(fundingAdvisor || hasChatSession) && isSubmenuOpen"
      data-cy="floating-button-submenu"
      :funding-advisor="fundingAdvisor"
      :has-chat-session="hasChatSession"
      @click:ai-chat="toggleChat"
      @click:funding-advisor="handleContactFundingAdvisor"
    />
  </div>

  <template v-if="fundingAdvisor && isFundingAdvisorDetailsOpen">
    <div class="fixed top-0 left-0 h-screen w-screen bg-primary-heavy-contrast/90"></div>
    <div class="absolute bottom-4 left-1/2 w-[90vw] -translate-x-1/2">
      <platform-contact-details
        :advisor="fundingAdvisor"
        @close="isFundingAdvisorDetailsOpen = false"
      />
    </div>
  </template>

  <div class="pointer-events-none fixed right-0 bottom-0 z-60">
    <Transition :name="isMobile ? 'slide-up' : 'slide-in'">
      <div
        v-if="isChatOpen"
        class="z-50"
        :class="[
          'chat-window-container',
          isMobile ? 'fixed inset-0' : 'fixed right-4 bottom-20 h-[70vh] w-120 shadow-md',
        ]"
      >
        <popup-chat-window @close="isChatOpen = false" />
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useStorage } from "@vueuse/core";
import { useI18n } from "vue-i18n";
import { useResponsive } from "@/composables/common";
import { useChatState } from "@/composables/state/chatSessionState";
import useApplicationsState from "@/composables/state/applicationsState";
import { sleep } from "@/util/common";

import IconChat from "@/components/icons/IconChat.vue";
import IconHeadset from "@/components/icons/IconHeadset.vue";
import IconX from "@/components/icons/IconX.vue";
import PlatformContactDetails from "@/components/platform/PlatformContactDetails.vue";
import PlatformFloatingButtonSubmenu from "@/components/platform/platform-floating-button/PlatformFloatingButtonSubmenu.vue";
import PopupChatToast from "@/components/chat/popup/PopupChatToast.vue";
import PopupChatWindow from "@/components/chat/popup/PopupChatWindow.vue";

const SHOW_TOAST_AFTER_MS = 3000;
const HIDE_TOAST_AFTER_MS = 10000;

const { t } = useI18n();
const { isMobile } = useResponsive();
const { selectedApplication } = useApplicationsState();
const { hasSession: hasChatSession } = useChatState();
const hasToastBeenShown = useStorage("hasToastBeenShown", false);

const isToastTriggered = ref(false);
const isChatOpen = ref(false);
const isFundingAdvisorDetailsOpen = ref(false);
const isSubmenuOpen = ref(false);

const fundingAdvisor = computed(() => selectedApplication.value?.funding_advisor);

const showToast = computed(
  () => isToastTriggered.value && !isChatOpen.value && !isSubmenuOpen.value
);

const handleFloatingButtonClick = () => {
  const action = isMobile.value ? toggleSubmenu : toggleChat;
  action();

  isToastTriggered.value = false;
};

const handleContactFundingAdvisor = () => {
  isSubmenuOpen.value = false;
  isFundingAdvisorDetailsOpen.value = true;
};

const toggleChat = () => {
  isChatOpen.value = !isChatOpen.value;
};

const toggleSubmenu = () => {
  isSubmenuOpen.value = !isSubmenuOpen.value;
};

watch(isMobile, () => {
  if (!isMobile.value && isSubmenuOpen.value) {
    toggleSubmenu();
  }
});

onMounted(async () => {
  if (hasToastBeenShown.value) {
    return;
  }

  await sleep(SHOW_TOAST_AFTER_MS);
  isToastTriggered.value = true;
  hasToastBeenShown.value = true;
  await sleep(HIDE_TOAST_AFTER_MS);
  isToastTriggered.value = false;
});
</script>

<style scoped>
@reference "@/tailwind.css";

.chat-window-wrapper {
  position: fixed;
  bottom: 0;
  right: 0;
  pointer-events: none;
}

.chat-window-container {
  pointer-events: auto;
  will-change: transform, opacity;
  transform-origin: bottom right;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease;
  backface-visibility: hidden;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
}

.slide-in-enter-active,
.slide-in-leave-active {
  transition:
    opacity 0.3s,
    transform 0.3s;
  backface-visibility: hidden;
}

.slide-in-enter-from,
.slide-in-leave-to {
  opacity: 0;
  transform: translateX(30px);
}
</style>
